// A draggable, resizable picture-in-picture window above the town square. Position is remembered
// per `storageKey`; size is the browser's native resize and isn't persisted.

import { GripHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type Pos = { x: number; y: number };

const MARGIN = 8;

function load(key: string): Pos | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw) as Pos;
    return typeof p.x === "number" && typeof p.y === "number" ? p : null;
  } catch {
    return null;
  }
}

function clamp(pos: Pos, el: HTMLElement | null): Pos {
  const w = el?.offsetWidth ?? 320;
  const h = el?.offsetHeight ?? 200;
  return {
    x: Math.min(Math.max(MARGIN, pos.x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
    y: Math.min(Math.max(MARGIN, pos.y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
  };
}

export function FloatingWindow({
  title,
  onClose,
  storageKey,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  storageKey: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos>(
    () => load(storageKey) ?? { x: Math.max(MARGIN, window.innerWidth - 380), y: 96 },
  );
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  // Keep it on screen if the window shrinks or the saved spot came from a bigger screen.
  useEffect(() => {
    const fit = () => setPos((p) => clamp(p, ref.current));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("button")) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    },
    [pos.x, pos.y],
  );
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setPos(clamp({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy }, ref.current));
  }, []);
  const onPointerUp = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    try {
      localStorage.setItem(storageKey, JSON.stringify(pos));
    } catch {
      /* position just won't be remembered */
    }
  }, [pos, storageKey]);

  return (
    <div
      ref={ref}
      style={{ left: pos.x, top: pos.y }}
      className="fixed z-40 flex h-[26rem] min-h-48 w-88 min-w-64 max-w-[95vw] resize flex-col overflow-hidden rounded-lg border border-brass/40 bg-ink-soft/95 shadow-2xl backdrop-blur"
    >
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="flex shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b border-ink-line px-3 py-2 active:cursor-grabbing"
      >
        <GripHorizontal className="size-4 text-parchment-dim" />
        <span className="flex-1 text-sm font-medium text-parchment">{title}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-parchment-dim hover:text-parchment"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-3">{children}</div>
    </div>
  );
}
